/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the WYSIWYG paper.

   Real page space, real margins, real wrap — the SAME shared brains the
   export uses (`shared/zone-layout.ts` via paper.tsx's ZoneBand, and
   `shared/body-layout.ts` via StudioBody). No "PAGE SPACE COLLAPSED"
   marker exists anywhere in this file; if the paper and the .docx ever
   disagree, that is a bug by definition (contract §3 R6).

   The header/footer/pre/post bands are the Designer's own zone objects
   (ZoneShell + ZoneBand) — click to select, drag tokens onto them, the
   same snap targets — reused, not rebuilt. Only the body differs: the
   Studio is not editing a document, so the body is the realistic sample
   (StudioBody) instead of a TipTap surface.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { StandardConfig } from '../../shared/types';
import { CanvasSelectionContext } from '../components/TokenComponents';
import { EndOfSection, ZoneBand, paperVars } from '../designer/paper';
import { ZoneShell } from '../designer/DesignerCanvas';
import type { FormaEdit } from '../designer/DesignPalettes';
import {
  bandVariantName,
  readZone,
  zoneKeyFor,
  type BandKind,
  type ZoneColumn,
} from '../designer/formaZonesModel';
import type { DesignWiring } from '../designer/Workspace';
import { STUDIO_SAMPLE_SPEC_NAME, STUDIO_SAMPLE_SPEC_NUMBER } from './sampleSpec';
import { StudioBody } from './StudioBody';
import { Ruler } from './Ruler';
import { ZoneSpacer } from './ZoneSpacer';
import { BlankPagePreview } from './BlankPagePreview';

export interface StudioCanvasProps {
  forma: StandardConfig | null;
  zoom: number;
  design: DesignWiring;
  levelFocus: number | null;
  onLevelClick: (level: number) => void;
  /** Drives the ruler's drag handles and the whole-zone spacing bars —
   *  both are ordinary undoable forma edits (contract §2.7). */
  edit: FormaEdit;
  /** Ctrl+wheel zoom (Order 2, 2026-08-13) — one step per notch, through the
   *  SAME clamped setter the toolbar's +/− buttons use. */
  onZoomDelta?: (step: number) => void;
  /** Clicking empty canvas clears EVERYTHING selected, the level highlight
   *  included — one selection system, never sticky (DESIGN.md §3.4). The
   *  level lived in the Studio's own state, so click-away used to leave it
   *  lit (Josh, 2026-08-13). */
  onClearLevel?: () => void;
}

export function StudioCanvas({
  forma,
  zoom,
  design,
  levelFocus,
  onLevelClick,
  edit,
  onZoomDelta,
  onClearLevel,
}: StudioCanvasProps) {
  const canvasSel = React.useContext(CanvasSelectionContext);
  // ─── Marquee select (restored 2026-08-13) ───
  // Drag a box across empty canvas to select every token it touches; Ctrl or
  // Shift adds to the current selection; a click that never moved clears
  // everything. Ported from the classic canvas's beginMarquee (app.tsx) —
  // the ONLY way to grab a whole header's worth of tokens in one gesture.
  const [marquee, setMarquee] = React.useState<{
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  } | null>(null);

  const beginMarquee = React.useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const tgt = e.target as HTMLElement;
      if (tgt.closest('[data-token-key]')) return; // a token handles its own drag
      if (tgt.closest('input,textarea,select,button,[role="switch"],[role="menu"]')) return;
      const additive = e.ctrlKey || e.metaKey || e.shiftKey;
      const x0 = e.clientX;
      const y0 = e.clientY;
      let moved = false;
      const onMove = (ev: MouseEvent) => {
        if (!moved && (Math.abs(ev.clientX - x0) > 4 || Math.abs(ev.clientY - y0) > 4))
          moved = true;
        if (moved) setMarquee({ x0, y0, x1: ev.clientX, y1: ev.clientY });
      };
      const onUp = (ev: MouseEvent) => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        setMarquee(null);
        if (!moved) {
          // A plain click on empty canvas clears EVERYTHING — never sticky
          // (§3.4): tokens, the zone selection, AND the level highlight.
          canvasSel.select(null);
          design.onStyleSelect(null);
          onClearLevel?.();
          return;
        }
        const rx0 = Math.min(x0, ev.clientX);
        const rx1 = Math.max(x0, ev.clientX);
        const ry0 = Math.min(y0, ev.clientY);
        const ry1 = Math.max(y0, ev.clientY);
        const hits: string[] = [];
        document.querySelectorAll('[data-token-key]').forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.left < rx1 && r.right > rx0 && r.top < ry1 && r.bottom > ry0) {
            const k = el.getAttribute('data-token-key');
            if (k) hits.push(k);
          }
        });
        canvasSel.selectMany(hits, additive);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [canvasSel, design, onClearLevel]
  );
  // Native non-passive listener — React's synthetic onWheel is passive, so
  // preventDefault can't stop the browser's own page-zoom there (ported
  // verbatim from the classic canvas's wheelZoomRef, app.tsx ~745).
  const wheelZoomRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      if (!el || !onZoomDelta) return;
      el.addEventListener(
        'wheel',
        (e: WheelEvent) => {
          if (!e.ctrlKey) return;
          e.preventDefault();
          onZoomDelta(e.deltaY < 0 ? 0.05 : -0.05);
        },
        { passive: false }
      );
    },
    [onZoomDelta]
  );
  const paperContext = {
    forma,
    specNumber: STUDIO_SAMPLE_SPEC_NUMBER,
    specName: STUDIO_SAMPLE_SPEC_NAME,
    projectMeta: {},
  };
  const bandVariant = bandVariantName(design.pageVariant);

  const bandEmpty = React.useCallback(
    (band: BandKind): boolean => {
      if (!forma) return false;
      return (['L', 'C', 'R'] as ZoneColumn[]).every(
        (c) => readZone(forma, zoneKeyFor(band, design.pageVariant, c)).length === 0
      );
    },
    [forma, design.pageVariant]
  );

  const wrapBand = (band: BandKind, node: React.ReactNode) => (
    <ZoneShell band={band} design={design} empty={bandEmpty(band)}>
      {node}
    </ZoneShell>
  );

  // Post-body zones are TOGGLABLE (Section End palette's "Enable post-body
  // zones" switch) — same field the classic canvas gated on
  // (`special.postBodyEnabled`) and the SAME field word-engine.ts reads at
  // export (`postGate`, line ~1959). Undefined falls back to "has content"
  // so nothing silently disappears; the explicit toggle always wins. OFF
  // must remove the band from the canvas entirely — not just empty its
  // content — exactly like the classic canvas's `{postOn && (...)}`.
  const sp = forma?.special;
  const hasPost = !!(sp?.postLeft?.length || sp?.postCenter?.length || sp?.postRight?.length);
  const postOn = sp?.postBodyEnabled ?? hasPost;

  const sheetClasses = ['dsg-sheet', 'dsg-zones-live', levelFocus ? `dsg-sel-l${levelFocus}` : '']
    .filter(Boolean)
    .join(' ');

  if (!forma) return null;

  return (
    <div
      className="dsg-canvas sfs-studio-canvas"
      style={{ ...paperVars(forma) }}
      ref={wheelZoomRef}
      onMouseDown={beginMarquee}
    >
      <div className="sfs-canvas-scale" style={{ transform: `scale(${zoom})` }}>
        <Ruler forma={forma} levelFocus={levelFocus} edit={edit} />

        <div className={sheetClasses}>
          <div className="dsg-sheet-header">
            {wrapBand(
              'header',
              <ZoneBand
                kind="header"
                variant={bandVariant}
                interactive
                edit={edit}
                onRequestTextEdit={design.onRequestTextEdit}
                onZoneInsertAt={design.onZoneInsertAt}
                onZoneReplaceAt={design.onZoneReplaceAt}
                {...paperContext}
              />
            )}
            <ZoneSpacer forma={forma} region="header" edge="after" onEdit={edit} />
          </div>

          <div className="dsg-sheet-body">
            <ZoneSpacer forma={forma} region="pre" edge="before" onEdit={edit} />
            {wrapBand(
              'pre',
              <ZoneBand
                kind="pre"
                interactive
                edit={edit}
                onRequestTextEdit={design.onRequestTextEdit}
                onZoneInsertAt={design.onZoneInsertAt}
                onZoneReplaceAt={design.onZoneReplaceAt}
                {...paperContext}
              />
            )}
            <ZoneSpacer forma={forma} region="pre" edge="after" onEdit={edit} />
            <StudioBody
              forma={forma}
              levelFocus={levelFocus}
              onLevelClick={onLevelClick}
              edit={edit}
            />
            {postOn && (
              <>
                <ZoneSpacer forma={forma} region="post" edge="before" onEdit={edit} />
                {wrapBand(
                  'post',
                  <ZoneBand
                    kind="post"
                    interactive
                    edit={edit}
                    onRequestTextEdit={design.onRequestTextEdit}
                    onZoneInsertAt={design.onZoneInsertAt}
                    onZoneReplaceAt={design.onZoneReplaceAt}
                    {...paperContext}
                  />
                )}
                <ZoneSpacer forma={forma} region="post" edge="after" onEdit={edit} />
              </>
            )}
            <EndOfSection
              {...paperContext}
              onConfigure={design.onOpenPanel ? () => design.onOpenPanel?.('eos') : undefined}
            />
          </div>

          <div className="dsg-sheet-footer">
            <ZoneSpacer forma={forma} region="footer" edge="before" onEdit={edit} />
            {wrapBand(
              'footer',
              <ZoneBand
                kind="footer"
                variant={bandVariant}
                interactive
                edit={edit}
                onRequestTextEdit={design.onRequestTextEdit}
                {...paperContext}
              />
            )}
          </div>
        </div>

        {forma.special?.blankPageEnabled && (
          <div className="sfs-blankpage-wrap">
            <BlankPagePreview forma={forma} />
          </div>
        )}
      </div>

      {/* The marquee rectangle — drawn from raw client coordinates, so it
          MUST live on <body>: inside the dock panel, an ancestor transform
          turns position:fixed into "fixed relative to that ancestor" and
          the box lands offset from the real mouse (Josh, 2026-08-17 — the
          zoomed-canvas variant of the same bug class §4 portals exist for).
          Hit-testing always used true viewport rects; only the drawing
          was off. */}
      {marquee &&
        createPortal(
          <div
            className="sfs-marquee"
            aria-hidden="true"
            style={{
              left: Math.min(marquee.x0, marquee.x1),
              top: Math.min(marquee.y0, marquee.y1),
              width: Math.abs(marquee.x1 - marquee.x0),
              height: Math.abs(marquee.y1 - marquee.y0),
            }}
          />,
          document.body
        )}
    </div>
  );
}
